import { useState } from 'react'
import { api, type Betroffen, type KorrekturAngebot, type KorrekturVorschlag } from '../api'

function datum(b: { aufgenommenAm: string; zeitzone: string }) {
  return new Date(b.aufgenommenAm).toLocaleDateString('de-DE', { timeZone: b.zeitzone, weekday: 'short', day: 'numeric', month: 'numeric', year: '2-digit' })
}

/**
 * Eine Schreibweise „falsch → richtig“ samt der Notizen, in denen „falsch“ vorkommt. Alle sind vorausgewählt; was
 * abgewählt wird (weil das Wort dort richtig ist), bleibt unverändert.
 */
function Ersetzung({
  falsch,
  richtig,
  notizen,
  frage,
  onAnwenden,
  onVerwerfen,
  verwerfenText,
  weitere = true,
}: {
  falsch: string
  richtig: string
  notizen: Betroffen[]
  frage: string
  onAnwenden: (notizIds: number[]) => Promise<unknown>
  onVerwerfen: () => Promise<unknown> | void
  verwerfenText: string
  /** Sind die betroffenen Notizen „weitere“ neben der, in der korrigiert wurde? */
  weitere?: boolean
}) {
  const [auswahl, setAuswahl] = useState<Set<number>>(() => new Set(notizen.map((n) => n.id)))
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const aktion = async (f: () => Promise<unknown> | void) => {
    setLaeuft(true)
    setFehler(null)
    try {
      await f()
    } catch (e) {
      setFehler((e as Error).message)
      setLaeuft(false)
    }
  }

  const umschalten = (id: number) =>
    setAuswahl((a) => {
      const neu = new Set(a)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  return (
    <article className="karte klaerung korrektur">
      <p>
        {frage} <strong>„{falsch}“</strong> → <strong>„{richtig}“</strong>
      </p>
      {notizen.length === 0 ? (
        <p className="leise klein">Kommt in keiner anderen Notiz vor. Künftige Aufnahmen schreiben es richtig.</p>
      ) : (
        <>
          <p className="leise klein">
            {notizen.length === 1 ? `Eine ${weitere ? 'weitere ' : ''}Notiz enthält` : `${notizen.length} ${weitere ? 'weitere ' : ''}Notizen enthalten`} „{falsch}“. Ausgewählte
            werden korrigiert und neu ausgewertet:
          </p>
          <ul className="betroffene">
            {notizen.map((n) => (
              <li key={n.id}>
                <label className="auswahl-zeile">
                  <input type="checkbox" checked={auswahl.has(n.id)} onChange={() => umschalten(n.id)} />
                  <span>
                    <span className="leise klein">{datum(n)}</span> {n.auszug}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      {fehler && <p className="fehler">{fehler}</p>}
      <div className="auswahl">
        <button disabled={laeuft} onClick={() => aktion(() => onAnwenden([...auswahl]))}>
          {auswahl.size === 0 ? 'Schreibweise merken' : auswahl.size === 1 ? 'Eine Notiz korrigieren' : `${auswahl.size} Notizen korrigieren`}
        </button>
        <button className="link" disabled={laeuft} onClick={() => aktion(onVerwerfen)}>
          {verwerfenText}
        </button>
      </div>
    </article>
  )
}

/** Nach dem Korrigieren eines Transkripts: dieselbe Korrektur in anderen Notizen anbieten */
export function KorrekturAngebote({
  angebote,
  onFertig,
  frage = 'Auch in anderen Notizen korrigieren?',
  weitere = true,
}: {
  angebote: KorrekturAngebot[]
  onFertig: () => void
  frage?: string
  weitere?: boolean
}) {
  const [offen, setOffen] = useState(angebote)
  const erledigt = (a: KorrekturAngebot) => {
    const rest = offen.filter((x) => x !== a)
    setOffen(rest)
    if (rest.length === 0) onFertig()
  }
  return (
    <div className="korrektur-angebote">
      {offen.map((a) => (
        <Ersetzung
          key={`${a.falsch}→${a.richtig}`}
          falsch={a.falsch}
          richtig={a.richtig}
          notizen={a.notizen}
          frage={frage}
          weitere={weitere}
          onAnwenden={async (ids) => {
            await api.korrekturAnwenden(a.falsch, a.richtig, ids)
            erledigt(a)
          }}
          onVerwerfen={() => erledigt(a)}
          verwerfenText="Nur hier"
        />
      ))}
    </div>
  )
}

/** Gesprochene Korrekturen aus Notizen, die noch bestätigt werden müssen (bei den Rückfragen) */
export function KorrekturVorschlaege({ vorschlaege, onGeklaert }: { vorschlaege: KorrekturVorschlag[]; onGeklaert: () => void }) {
  return (
    <section className="klaerungen">
      <h2>{vorschlaege.length === 1 ? 'Eine Korrektur' : `${vorschlaege.length} Korrekturen`} aus deinen Notizen</h2>
      {vorschlaege.map(({ vorschlag: v, notizen }) => (
        <Ersetzung
          key={v.id}
          falsch={v.falsch}
          richtig={v.richtig}
          notizen={notizen}
          frage={`Am ${datum(v)} hast du gesagt:`}
          onAnwenden={async (ids) => {
            await api.korrekturVorschlagAnnehmen(v.id, ids)
            onGeklaert()
          }}
          onVerwerfen={async () => {
            await api.korrekturVorschlagAblehnen(v.id)
            onGeklaert()
          }}
          verwerfenText="Nicht korrigieren"
        />
      ))}
    </section>
  )
}
